/**
 * Shareable PDF buttons (managed on the admin panel's PDFs page).
 *
 * Tapping SHARES the PDF straight to WhatsApp (or any app on the share sheet)
 * instead of opening a viewer, because that's what dealers actually do with it.
 *   - Native APK: download → cache file → @capacitor/share share sheet.
 *   - Web:        navigator.share({ files }) where supported.
 *   - Fallback:   download the PDF so it's never a dead end.
 */
import { useState } from 'react';
import type { OfferPdf } from '@mitang/shared';
import { useOfferPdfs } from '../../hooks/useOffers';
import { logActivity } from '../../lib/activity/activityLogger';
import { isNative } from '../../lib/platform';

/** Button colours cycle so several PDFs stay visually distinct. */
const TONES = [
  'bg-brand-light text-brand-dark',
  'bg-amber-50 text-stale',
  'bg-emerald-50 text-drop',
  'bg-slate-100 text-slate-600',
];

function fileNameFor(pdf: OfferPdf): string {
  const safe = pdf.title.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-') || pdf.id;
  return `${safe}.pdf`;
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

async function shareNative(blob: Blob, name: string): Promise<boolean> {
  try {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ]);
    const file = await Filesystem.writeFile({
      path: name,
      data: await blobToBase64(blob),
      directory: Directory.Cache,
    });
    await Share.share({ files: [file.uri] });
    return true;
  } catch {
    return false;
  }
}

async function shareWeb(blob: Blob, name: string): Promise<boolean> {
  try {
    const file = new File([blob], name, { type: 'application/pdf' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file] });
      return true;
    }
  } catch (err) {
    // User dismissed the sheet — treat as handled, don't also download.
    if (err instanceof Error && err.name === 'AbortError') return true;
  }
  return false;
}

export function OfferButtons({ category }: { category: string }): JSX.Element | null {
  const pdfs = useOfferPdfs();
  const [busyId, setBusyId] = useState<string | null>(null);

  if (pdfs.length === 0) return null;

  const share = async (pdf: OfferPdf): Promise<void> => {
    setBusyId(pdf.id);
    logActivity('shared_product', { offer: pdf.id, category });
    try {
      const res = await fetch(pdf.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const name = fileNameFor(pdf);
      const shared = isNative ? await shareNative(blob, name) : await shareWeb(blob, name);
      if (!shared) {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = name;
        a.rel = 'noopener';
        a.click();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
      }
    } catch {
      window.open(pdf.url, '_blank', 'noopener,noreferrer');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-2 px-4 pb-1 pt-2">
      {pdfs.map((pdf, i) => (
        <button
          key={pdf.id}
          type="button"
          disabled={busyId !== null}
          className={`min-w-[45%] flex-1 rounded-full px-3 py-2.5 text-[11.5px] font-extrabold uppercase tracking-[.04em] disabled:opacity-60 ${TONES[i % TONES.length]}`}
          onClick={() => void share(pdf)}
        >
          {busyId === pdf.id ? 'Preparing…' : pdf.title}
        </button>
      ))}
    </div>
  );
}
